import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import jsPDF from 'jspdf';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { PdfServiceService } from 'src/app/core/_services/pdfService.service';

@Component({
  selector: 'app-image-viewer-dialog',
  templateUrl: './image-viewer-dialog.component.html',
  styleUrls: ['./image-viewer-dialog.component.scss']
})
export class ImageViewerDialogComponent implements OnInit {
  @ViewChild('contentToCapture') contentToCapture: ElementRef;
  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement>;

  imageUrl: string = '';
  pdfDoc: jsPDF;

  constructor(
    public dialogRef: MatDialogRef<ImageViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private pdf: PdfServiceService,
    private loader: NgxUiLoaderService
  ) {
    this.pdfDoc = new jsPDF();
  }

  fromHelpSupport: boolean = false;
  pdfBackgroundImage: any = 'assets/images/pdf.png';
  mediaUrl: string = '';
  mediaType: 'image' | 'video' | 'pdf' = 'image';
  fileName: string = '';
  // Video extensions to detect
  private readonly VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];

  async ngOnInit() {
    console.log(this.data);

    // Handle both object shape and plain string (backwards compatibility)
    if (this.data && typeof this.data === 'object' && this.data.video) {
      this.mediaUrl = this.data.video;
      this.fileName = this.data.uniqueName || 'video.mp4';
      this.mediaType = 'video';
    } else {
      this.mediaUrl = this.data;
      this.fileName = this.getFileName(this.data);
      if (this.isPdf(this.data)) {
        this.mediaType = 'pdf';
        await this.getBackgroundImage(this.data);
      } else if (this.isVideo(this.data)) {
        this.mediaType = 'video';
      } else {
        this.mediaType = 'image';
      }
    }

    this.imageUrl = this.mediaUrl;

    if (this.router.url.includes('help_support')) {
      this.fromHelpSupport = true;
    }
  }

  /** Returns true if the file is a video */
  isVideo(file: string): boolean {
    if (!file) return false;
    const lower = file.toLowerCase().split('?')[0]; // strip query params
    return this.VIDEO_EXTENSIONS.some(ext => lower.endsWith(ext));
  }

  /** Extracts filename from URL for download attribute */
  getFileName(url: string): string {
    try {
      const parts = url.split('?')[0].split('/');
      return parts[parts.length - 1] || 'video';
    } catch {
      return 'video';
    }
  }

  async getBackgroundImage(url: string) {
    this.pdfBackgroundImage = await this.pdf.extractFirstPageAsImage(url);
    return this.pdfBackgroundImage;
  }

  isPdf(file: string): boolean {
    return /\.pdf$/i.test(file);
  }

  downloadImage() {
    const image = document.getElementById('image') as HTMLImageElement;
    const url = image.src;
    const link = document.createElement('a');
    link.href = url;
    link.download = 'image.jpg';
    document.body.appendChild(link);
    link.style.display = 'none';
    link.click();
    document.body.removeChild(link);
  }

  async downLoadImage(): Promise<void> {
    try {
      this.loader.start();
      const response = await fetch(decodeURIComponent(this.imageUrl + '?crossorigin'));
      if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      this.imageUrl = imageUrl;
      this.downloadImageToPdf();
    } catch (err) {
      this.loader.stop();
      console.error('Error downloading image', err);
    }
  }

  downloadImageToPdf() {
    this.loader.stop();
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = this.imageUrl;

    img.onload = () => {
      const pdfDoc = this.pdfDoc;
      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const pageHeight = pdfDoc.internal.pageSize.getHeight();
      const imgAspectRatio = img.width / img.height;
      let newWidth, newHeight;

      if (imgAspectRatio > pageWidth / pageHeight) {
        newWidth = pageWidth;
        newHeight = newWidth / imgAspectRatio;
      } else {
        newHeight = pageHeight;
        newWidth = newHeight * imgAspectRatio;
      }

      const x = (pageWidth - newWidth) / 2;
      const y = (pageHeight - newHeight) / 2;

      pdfDoc.addImage(img, 'JPEG', x, y, newWidth, newHeight);
      pdfDoc.save('image.pdf');
    };
  }
}
